import { z } from "zod";
import { isoDateTimeString, uuidSchema } from "./common";
import { PLAN_CODES, type PlanCode } from "../catalogs/plans";

// A subscription plan ("gói") as configured in the SubscriptionPlan table.
// `code` is a stable machine key; everything else is admin-editable.
export const subscriptionPlanSchema = z.object({
  id: uuidSchema,
  code: z.enum(PLAN_CODES),
  name: z.string(),
  priceVnd: z.number().int().nonnegative(),
  // Optional struck-through "before" price for marketing (null = none).
  originalPriceVnd: z.number().int().nonnegative().nullable(),
  durationDays: z.number().int().nonnegative(),
  sourcingQuota: z.number().int().nonnegative(),
  maxConcurrentPostings: z.number().int().nonnegative(),
  boostQuota: z.number().int().nonnegative(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
});
export type SubscriptionPlan = z.infer<typeof subscriptionPlanSchema>;

// An org's current subscription row (null expiresAt = FREE / never expires).
export const orgSubscriptionSchema = z.object({
  id: uuidSchema,
  planId: uuidSchema,
  startedAt: isoDateTimeString,
  expiresAt: isoDateTimeString.nullable(),
  periodStartedAt: isoDateTimeString,
  sourcingUsedThisPeriod: z.number().int().nonnegative(),
  boostUsedThisPeriod: z.number().int().nonnegative(),
  // À-la-carte balances that carry over (don't reset each period).
  extraSourcing: z.number().int().nonnegative(),
  extraBoost: z.number().int().nonnegative(),
});
export type OrgSubscription = z.infer<typeof orgSubscriptionSchema>;

// What the employer app shows on the "gói" screen: the effective plan (after
// expiry fallback), the raw subscription row, and quota usage.
export const orgPlanStatusSchema = z.object({
  // Effective plan code AFTER applying expiry (expired PRO/BIZ → FREE).
  effectiveCode: z.enum(PLAN_CODES),
  plan: subscriptionPlanSchema,
  subscription: orgSubscriptionSchema.nullable(),
  expiresAt: isoDateTimeString.nullable(),
  // Days until expiry (negative if already expired, null if FREE/no expiry).
  daysUntilExpiry: z.number().int().nullable(),
  // Total remaining = (plan quota - used this period) + à-la-carte extra.
  sourcingRemaining: z.number().int().nonnegative(),
  boostRemaining: z.number().int().nonnegative(),
  // À-la-carte balances (carry over), broken out so the UI can show "gói + mua
  // thêm" separately from the plan-period remainder.
  extraSourcing: z.number().int().nonnegative(),
  extraBoost: z.number().int().nonnegative(),
});
export type OrgPlanStatus = z.infer<typeof orgPlanStatusSchema>;

// Subscribe / renew request: which plan to buy + where the payment provider
// should send the user back (prod only; ignored on dev/simulated orders).
export const subscribeRequestSchema = z.object({
  planCode: z.enum(PLAN_CODES),
  returnUrl: z.string().url().optional(),
  cancelUrl: z.string().url().optional(),
  errorUrl: z.string().url().optional(),
  // LEGAL-04: the buyer must tick "đã đọc Điều khoản thanh toán và đồng ý đặt
  // mua" before an order is created. The server rejects anything but true and
  // logs an ORDER_CONFIRM consent — the UI checkbox alone is not trusted.
  consentAccepted: z.literal(true),
});
export type SubscribeRequest = z.infer<typeof subscribeRequestSchema>;

// Buy an à-la-carte top-up (extra sourcing or boost). `packCode` references
// SOURCING_PACKS / BOOST_PACKS in the plans catalog.
export const buyTopupRequestSchema = z.object({
  kind: z.enum(["SOURCING", "BOOST"]),
  packCode: z.string().min(1).max(40),
  returnUrl: z.string().url().optional(),
  cancelUrl: z.string().url().optional(),
  errorUrl: z.string().url().optional(),
  // LEGAL-04: same order-confirm consent as subscribe. Server-enforced.
  consentAccepted: z.literal(true),
});
export type BuyTopupRequest = z.infer<typeof buyTopupRequestSchema>;

export const planPurchaseStatusSchema = z.enum([
  "PENDING",
  "PAID",
  "EXPIRED",
  "FAILED",
]);
export type PlanPurchaseStatus = z.infer<typeof planPurchaseStatusSchema>;

// One purchase order — a plan renewal (kind PLAN) or an à-la-carte top-up
// (kind SOURCING/BOOST). planCode is set for PLAN; quantity for top-ups.
export const orgPlanPurchaseSchema = z.object({
  id: uuidSchema,
  orderCode: z.string(), // providerOrderId, e.g. PLAN-123 — used to fetch the QR
  kind: z.enum(["PLAN", "SOURCING", "BOOST"]),
  planCode: z.enum(PLAN_CODES).nullable(),
  quantity: z.number().int().nullable(),
  amountVnd: z.number().int().nonnegative(),
  status: planPurchaseStatusSchema,
  checkoutUrl: z.string().nullable(),
  createdAt: isoDateTimeString,
  paidAt: isoDateTimeString.nullable(),
});
export type OrgPlanPurchase = z.infer<typeof orgPlanPurchaseSchema>;

// A single order's full detail — for the invoice/result screen at
// /billing/:orderCode. Extends the list DTO with the receiving-account info and
// transfer memo (so the buyer can reconcile any status), the plan snapshot
// (name + durationDays, PLAN orders only, so the FE can show the validity
// window paidAt → paidAt + durationDays), and the payment deadline (for PENDING
// orders). Fetched via GET orgs/:orgId/plan/purchases/:orderCode — org-scoped.
export const orgPlanPurchaseDetailSchema = orgPlanPurchaseSchema.extend({
  // Plan snapshot for PLAN orders (null for SOURCING/BOOST top-ups).
  planName: z.string().nullable(),
  durationDays: z.number().int().nonnegative().nullable(),
  // Receiving bank account (one system-wide account, from VietQR config).
  accountNo: z.string(),
  accountName: z.string(),
  bankBin: z.string(),
  // Transfer memo — equals the order code; the Casso webhook matches on it.
  addInfo: z.string(),
  // Pay-by deadline (shown while PENDING).
  deadlineAt: isoDateTimeString,
});
export type OrgPlanPurchaseDetail = z.infer<typeof orgPlanPurchaseDetailSchema>;

// Response to POST subscribe: the created order plus (on prod) the Sepay
// hosted-checkout handoff the FE submits. On dev/simulated orders `checkout` is
// null — the FE calls the confirm endpoint directly instead of redirecting.
export const createPlanPurchaseResponseSchema = z.object({
  purchase: orgPlanPurchaseSchema,
  checkout: z
    .object({
      url: z.string(),
      formFields: z.record(z.string(), z.string()),
    })
    .nullable(),
  // True when SEPAY is simulated (dev): the FE can skip checkout and confirm.
  simulated: z.boolean(),
});
export type CreatePlanPurchaseResponse = z.infer<
  typeof createPlanPurchaseResponseSchema
>;

// The buyable plan catalog for the employer "gói" screen: active, non-FREE
// plans (price + quotas + optional struck-through originalPriceVnd), sourced
// live from the SubscriptionPlan table so admin price edits show up without a
// deploy. Ordered by sortOrder.
export const planCatalogResponseSchema = z.object({
  items: z.array(subscriptionPlanSchema),
});
export type PlanCatalogResponse = z.infer<typeof planCatalogResponseSchema>;

// An à-la-carte top-up pack as sold on the public pricing page (mirrors
// SOURCING_PACKS / BOOST_PACKS in the plans catalog).
export const topupPackSchema = z.object({
  code: z.string().min(1).max(40),
  quantity: z.number().int().positive(),
  priceVnd: z.number().int().nonnegative(),
});
export type TopupPackDto = z.infer<typeof topupPackSchema>;

// Unauthenticated pricing feed for the marketing site (`/bang-gia`). Unlike the
// employer upgrade modal this INCLUDES the FREE plan — the public table shows
// all three tiers side by side — and ships the top-up packs so the page never
// hardcodes prices.
export const publicPlansResponseSchema = z.object({
  plans: z.array(subscriptionPlanSchema),
  sourcingPacks: z.array(topupPackSchema),
  boostPacks: z.array(topupPackSchema),
});
export type PublicPlansResponse = z.infer<typeof publicPlansResponseSchema>;

// Purchase history for the employer billing screen — every order (PENDING /
// PAID / EXPIRED / FAILED) for the org, newest first.
export const planPurchaseHistoryResponseSchema = z.object({
  items: z.array(orgPlanPurchaseSchema),
});
export type PlanPurchaseHistoryResponse = z.infer<
  typeof planPurchaseHistoryResponseSchema
>;

// VietQR bank-transfer QR for a PENDING PLAN-<seq> order. The FE renders
// `qrDataUrl` (a base64 QR image) or `qrCode` (the raw EMVCo payload) and shows
// the transfer details so the customer can pay from any banking app. `addInfo`
// is the transfer memo — it MUST contain the order code so the Casso webhook can
// match the payment back to this order.
export const planPurchaseQrResponseSchema = z.object({
  orderCode: z.string(), // PLAN-<seq>
  amountVnd: z.number().int().nonnegative(),
  accountNo: z.string(),
  accountName: z.string(),
  bankBin: z.string(),
  addInfo: z.string(),
  qrCode: z.string(), // raw EMVCo QR string
  qrDataUrl: z.string().nullable(), // data:image/png;base64,... when template returns an image
});
export type PlanPurchaseQrResponse = z.infer<
  typeof planPurchaseQrResponseSchema
>;

// Response to the "I've paid" sync nudge. `synced` = Casso accepted the pull
// request; the order still settles via the webhook, so the FE keeps polling
// regardless.
export const planPurchaseSyncResponseSchema = z.object({
  synced: z.boolean(),
});
export type PlanPurchaseSyncResponse = z.infer<
  typeof planPurchaseSyncResponseSchema
>;

// The effective plan code an org is on RIGHT NOW. No auto-renew: once expiresAt
// has passed, PRO/BUSINESS silently fall back to FREE (computed, never written
// back to the row). A null expiresAt never expires. Pass `now` for testability.
export function effectivePlanCode(
  sub: { code: PlanCode; expiresAt: string | Date | null } | null,
  now: Date = new Date(),
): PlanCode {
  if (!sub) return "FREE";
  if (sub.expiresAt == null) return sub.code;
  const exp =
    typeof sub.expiresAt === "string" ? new Date(sub.expiresAt) : sub.expiresAt;
  return exp.getTime() > now.getTime() ? sub.code : "FREE";
}
